import type { ElementKey } from '~/types/game'
import { TUTORIAL_STARDUST } from '../../shared/economy'

/**
 * 🎓 v7.04 (per Javi, after a tester "couldn't find a tutorial or guide"):
 * the FIRST DISPATCH - a first-timer's guided run. No draft: Cinderfang
 * answers alone, one scripted swarm node teaches the loop in three beats (five since v8.02),
 * and finishing pays a booster's worth of Stardust. The run is a throwaway
 * in its own mode (`mode: 'tutorial'`): never counted, synced or boarded.
 */
/** 🎓 v7.20: 'dispatch', 'surge', 'pair' and 'skill' must be WON to advance -
 * a discard, an expiry or a loss deals the same lesson again; 'expire'
 * resolves either way, and a win there never counts toward the practice goal.
 * 🎓 v8.02 (per Javi): 'surge' teaches Cinderfang's Surge, 'skill' teaches
 * Rillhopper's Skim and the inspect glass. */
export type FirstDispatchLesson = 'dispatch' | 'expire' | 'surge' | 'pair' | 'skill'

export interface FirstDispatchBeat {
  lesson: FirstDispatchLesson
  /** A creature that joins the roster the moment this beat arrives. */
  join?: string
  required: Partial<Record<ElementKey, number>>
  slots: number
  /** Seconds before the mission lapses: long for the taught beats, short
   * for the one the player is told to let go. */
  fuse: number
  damage: number
  xp: number
  /** 📱 M3 (decision 0.9, pinned so the whole practice plays offline in the
   * apps): the foe this lesson always deals. Its idle, victory and defeat
   * loops ship in every app bundle (shared/starterSet.ts, TUTORIAL_ENEMIES -
   * tests/mobile-shell.test.ts keeps the two lists equal). One per lesson's
   * element, the lightest painted kits of their sets. */
  enemy: string
}

export const FIRST_DISPATCH = {
  hero: 'cinderfang',
  partner: 'rillhopper',
  nodeId: 'first-dispatch',
  stardust: TUTORIAL_STARDUST,
  /** Seconds between one beat ending and the next mission arriving. */
  beatGap: 1.6,
  /** 🎓 v7.40 (per Javi, D5): after lesson 2's "Got it" (the card holds the
   * clock), the escaping monster freezes the field this long on its way out:
   * "Good call. It only cost us N health." (v7.45) and the heart bar flashes
   * (v7.47: its own leaving line beside it - data/taunts.ts, by lesson). */
  gotAwayMs: 3000,
  /** 📱 M3 (decision 0.9): the practice's song - with the title theme, the
   * First Dispatch's two tracks. */
  battleTrack: '/music/battle1.mp3',
  // 🎓 v7.40 (per Javi): xp 0 on every beat - no level-ups and no unexplained
  // gold "+1" in the practice (grantXp also refuses a practice run).
  script: [
    // 1. Cinderfang covers it alone: open, assign, dispatch, watch the result.
    { lesson: 'dispatch', enemy: 'Ember Imps', required: { fire: 2 }, slots: 1, fuse: 600, damage: 2, xp: 0 },
    // 2. Cinderfang covers only 1 of the 4 lightning: let it lapse and feel the cost.
    // 🎓 v7.40 (D5): a 7 s fuse - the big countdown the player watches after "Got it".
    { lesson: 'expire', enemy: 'Volt Mite', required: { lightning: 4 }, slots: 1, fuse: 7, damage: 4, xp: 0 },
    // 3. 🎓 v8.02: 6 Fire - Cinderfang's 4 fall short until his Surge adds +2 (100%).
    { lesson: 'surge', enemy: 'Ember Imps', required: { fire: 6 }, slots: 1, fuse: 600, damage: 2, xp: 0 },
    // 4. Rillhopper joins: fire AND water - coverage needs both aboard.
    { lesson: 'pair', enemy: 'Flare Wisps', join: 'rillhopper', required: { fire: 2, water: 2 }, slots: 2, fuse: 600, damage: 2, xp: 0 },
    // 5. 🎓 v8.02: Rillhopper alone covers the 3 Water; the glass opens his
    // page, then Skim sends him with no travel at all. (join again: the
    // engine joins once, this guards the exhausted-script fallback.)
    { lesson: 'skill', enemy: 'Dew Drake', join: 'rillhopper', required: { water: 3 }, slots: 1, fuse: 600, damage: 2, xp: 0 },
  ] as FirstDispatchBeat[],
}
